/* WAVE variant picker (September 2026) - high variant support.

   Why this exists
   ---------------
   Shopify caps product.variants in Liquid at 250. The Unsen theme's own picker
   (T4SThemeSP.Product / Variants in theme.min.js) needs the entire variant list
   as JSON in the page, so it breaks above 250 variants. WAVE's nano bond products
   need up to 580 (58 colours x 5 lengths x 2 weights).

   How it works
   ------------
   snippets/product-form.liquid now renders the option swatches from
   product.options_with_values (product_option_value objects carry .id, .selected
   and .available) and a small hidden [data-wave-variant-state] element for the
   selected combination. The theme's swatch module is switched off for these
   products ("disableSwatch": true), so this file takes over:

     click swatch -> collect the selected option value ids
                  -> GET {product url}?section_id=wave-variant-state&option_values=a,b,c
                     (Shopify Section Rendering API, renders sections/wave-variant-state.liquid)
                  -> swap the state element, update swatches, price, add to cart button,
                     media, sticky bar, Klarna, URL and fire the events the rest of the
                     theme listens to.

   Everything the theme's ajax add to cart needs is the value of select[name="id"]
   inside the form, which this file keeps in sync. Nothing here touches the cart.

   Events fired (on the [data-product-featured] container, and on the form):
     - native  "wave:variant:changed"  detail: { variant, oldVariant, form }
     - jQuery  "variant:changed"       { currentVariant, oldVariant }   (theme contract)
     - jQuery  "variant:inventory", "pickupAvailability:update|clear",
               "notifyBackinStock:show|hide", "currency:update" on body (theme contract)
     - native  "change" on select[name="id"]  (WAVE inline scripts listen to this)
*/
(function () {
  'use strict';
  if (window.customElements && window.customElements.get('wave-variant-picker')) return;

  /* theme.min.js (jQuery_T4NT) and T4SProductStrings load after this file, so
     resolve them lazily every time they are needed. */
  function jQ() { return window.jQuery_T4NT || window.jQuery || null; }
  function strings() { return window.T4SProductStrings || {}; }
  function txt(key, fallback) { var s = strings(); return s[key] || fallback; }

  function attr(el, name, fallback) {
    if (!el) return fallback;
    var v = el.getAttribute(name);
    return v === null ? fallback : v;
  }
  function bool(v) { return v === true || v === 'true'; }
  function num(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }

  /* Read the [data-wave-variant-state] element into a plain object shaped like the
     variant JSON the theme used to work with, so any listener expecting
     currentVariant.id / .available / .price keeps working. */
  function readState(stateEl) {
    if (!stateEl) return null;
    var exists = bool(attr(stateEl, 'data-exists', 'false'));
    var v = {
      exists: exists,
      id: exists ? num(attr(stateEl, 'data-variant-id', '0')) : null,
      available: exists && bool(attr(stateEl, 'data-available', 'false')),
      preorder: bool(attr(stateEl, 'data-preorder', 'false')),
      title: attr(stateEl, 'data-title', ''),
      name: attr(stateEl, 'data-title', ''),
      sku: attr(stateEl, 'data-sku', ''),
      barcode: attr(stateEl, 'data-barcode', ''),
      price: num(attr(stateEl, 'data-price', '0')),
      compare_at_price: num(attr(stateEl, 'data-compare-at-price', '0')) || null,
      price_money: attr(stateEl, 'data-price-money', ''),
      compare_money: attr(stateEl, 'data-compare-money', ''),
      save_money: attr(stateEl, 'data-save-money', ''),
      save_percent: num(attr(stateEl, 'data-save-percent', '0')),
      klarna: attr(stateEl, 'data-klarna', ''),
      inventory_quantity: num(attr(stateEl, 'data-inventory-qty', '0')),
      inventory_policy: attr(stateEl, 'data-inventory-policy', '') || null,
      inventory_management: attr(stateEl, 'data-inventory-management', '') || null,
      max_qty: num(attr(stateEl, 'data-max-qty', '9999')) || 9999,
      media_id: attr(stateEl, 'data-media-id', ''),
      image: attr(stateEl, 'data-image', ''),
      image_width: num(attr(stateEl, 'data-image-width', '0')),
      image_height: num(attr(stateEl, 'data-image-height', '0')),
      url: attr(stateEl, 'data-url', ''),
      product_handle: attr(stateEl, 'data-product-handle', ''),
      collection_ids: attr(stateEl, 'data-collection-ids', ''),
      product_id: attr(stateEl, 'data-product-id', '')
    };
    v.featured_media = v.media_id ? { id: num(v.media_id) } : null;
    v.featured_image = v.image ? { src: v.image, width: v.image_width, height: v.image_height } : null;
    v.options = [];
    var ovs = stateEl.querySelectorAll('[data-wave-ov][data-selected="true"]');
    for (var i = 0; i < ovs.length; i++) v.options.push(attr(ovs[i], 'data-value', ''));
    v.option1 = v.options[0] || null; v.option2 = v.options[1] || null; v.option3 = v.options[2] || null;
    return v;
  }

  function jq(target, evt) {
    var $ = jQ();
    if (!$) return;
    try { $(target).trigger(evt); } catch (e) { /* ignore */ }
  }

  var Picker = function (root) {
    this.root = root;
    this.form = root.closest('form');
    this.container = root.closest('[data-product-featured]') || document;
    this.stateEl = root.querySelector('[data-wave-variant-state]');
    this.select = this.form ? this.form.querySelector('select[name="id"]') : null;
    this.productUrl = attr(root, 'data-product-url', '');
    this.sectionId = attr(root, 'data-section-id', 'wave-variant-state');
    this.current = readState(this.stateEl);
    this.pending = 0;
    this.inQuickView = !!root.closest('#t4s_temp_modal, .t4s-product-quick-view, .t4s-product-quick-shop, .t4s-modal');
    var cfg = {};
    try { cfg = JSON.parse(attr(this.container, 'data-product-featured', '{}')); } catch (e) { cfg = {}; }
    this.cfg = cfg;
    this.updateHistory = !this.inQuickView && document.body.classList.contains('template-product') && cfg.isMainProduct === true;
    this.bind();
    // Reflect the server rendered state once so button / sticky / Klarna match it.
    this.apply(this.current, null, true);
    // A ?variant= link should open on that variant's image, like the stock theme did.
    if (this.current && this.current.media_id && /[?&]variant=/.test(window.location.search)) this.showInitialMedia();
  };

  Picker.prototype.showInitialMedia = function () {
    var self = this, tries = 0;
    var main = this.container.querySelector('[data-main-media]');
    if (!main) return;
    var attempt = function () {
      tries++;
      if (main.classList.contains('flickityt4s-enabled') || tries > 60) { self.showMedia(self.current.media_id); return; }
      setTimeout(attempt, 100);
    };
    attempt();
  };

  Picker.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var item = e.target.closest('[data-swatch-item]');
      if (!item || !self.root.contains(item)) return;
      e.preventDefault();
      if (item.classList.contains('is--selected')) return;
      self.selectItem(item);
    });
    // WAVE colour dropdown (a plain <select> mirror of the colour swatches).
    var dds = this.root.querySelectorAll('[data-swatch-dropdown]');
    for (var i = 0; i < dds.length; i++) {
      dds[i].addEventListener('change', function (e) {
        var opt = e.target.options[e.target.selectedIndex];
        var id = opt && opt.getAttribute('data-option-value-id');
        if (!id) return;
        var item = self.root.querySelector('[data-swatch-item][data-option-value-id="' + id + '"]');
        if (item && !item.classList.contains('is--selected')) self.selectItem(item);
      });
    }
  };

  Picker.prototype.selectItem = function (item) {
    var group = item.closest('[data-swatch-option]');
    if (!group) return;
    var siblings = group.querySelectorAll('[data-swatch-item]');
    for (var i = 0; i < siblings.length; i++) siblings[i].classList.remove('is--selected');
    item.classList.add('is--selected');
    var cur = group.querySelectorAll('[data-current-value]');
    for (var j = 0; j < cur.length; j++) cur[j].textContent = attr(item, 'data-value', '');
    var dd = group.querySelector('[data-swatch-dropdown]');
    if (dd) {
      var id = attr(item, 'data-option-value-id', '');
      for (var k = 0; k < dd.options.length; k++) {
        if (dd.options[k].getAttribute('data-option-value-id') === id) { dd.selectedIndex = k; break; }
      }
    }
    this.fetchState();
  };

  Picker.prototype.selectedOptionValueIds = function () {
    var groups = this.root.querySelectorAll('[data-swatch-option]');
    var ids = [];
    for (var i = 0; i < groups.length; i++) {
      var sel = groups[i].querySelector('[data-swatch-item].is--selected[data-option-value-id]');
      if (sel) ids.push(attr(sel, 'data-option-value-id', ''));
    }
    return ids;
  };

  Picker.prototype.fetchState = function () {
    var self = this;
    var ids = this.selectedOptionValueIds();
    var url = this.productUrl + '?section_id=' + encodeURIComponent(this.sectionId) + '&option_values=' + ids.join(',');
    var token = ++this.pending;
    this.root.classList.add('is--wave-loading');
    if (this.form) this.form.classList.add('is--wave-loading');
    fetch(url, { headers: { 'Cache-Control': 'no-cache' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        if (token !== self.pending) return; // a newer click superseded this one
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-wave-variant-state]');
        if (!fresh) throw new Error('state missing');
        var oldVariant = self.current;
        var adopted = document.importNode(fresh, true);
        self.stateEl.parentNode.replaceChild(adopted, self.stateEl);
        self.stateEl = adopted;
        self.current = readState(adopted);
        self.syncSwatches(adopted);
        self.apply(self.current, oldVariant, false);
      })
      .catch(function (err) {
        if (window.console) console.warn('[wave-variant-picker]', err);
      })
      .then(function () {
        if (token !== self.pending) return;
        self.root.classList.remove('is--wave-loading');
        if (self.form) self.form.classList.remove('is--wave-loading');
      });
  };

  /* Mark every swatch from the server's availability map. Sold out combinations are
     shown crossed through (is--soldout), never hidden, and stay clickable. */
  Picker.prototype.syncSwatches = function (stateEl) {
    var map = stateEl.querySelectorAll('[data-wave-ov]');
    for (var i = 0; i < map.length; i++) {
      var id = attr(map[i], 'data-wave-ov', '');
      var items = this.root.querySelectorAll('[data-swatch-item][data-option-value-id="' + id + '"]');
      var available = bool(attr(map[i], 'data-available', 'true'));
      var selected = bool(attr(map[i], 'data-selected', 'false'));
      var img = attr(map[i], 'data-img', '');
      for (var j = 0; j < items.length; j++) {
        items[j].classList.toggle('is--soldout', !available);
        items[j].classList.toggle('is--selected', selected);
        if (img && attr(items[j], 'data-bg', '') !== img) {
          items[j].setAttribute('data-bg', img);
          items[j].style.backgroundImage = 'url(' + img + ')';
        }
        if (selected) {
          var group = items[j].closest('[data-swatch-option]');
          var cur = group ? group.querySelectorAll('[data-current-value]') : [];
          for (var k = 0; k < cur.length; k++) cur[k].textContent = attr(items[j], 'data-value', '');
        }
      }
    }
  };

  Picker.prototype.priceHtml = function (v) {
    var badge = '', STR = strings();
    var priceEl = this.container.querySelector('[data-product-price]');
    var saletype = priceEl ? attr(priceEl, 'data-saletype', '0') : '0';
    var onSale = v.compare_at_price && v.compare_at_price > v.price;
    if (onSale) {
      if (saletype === '1' && STR.badgeSavePercent2) badge = ' <span class="data-wpd-hide t4s-badge-price">' + STR.badgeSavePercent2.replace('[sale]', v.save_percent) + '</span>';
      else if (saletype === '2' && STR.badgeSaveFixed2) badge = ' <span class="data-wpd-hide t4s-badge-price">' + STR.badgeSaveFixed2.replace('[sale]', v.save_money) + '</span>';
      var insDel = priceEl && attr(priceEl, 'data-formart-price', '') === 'ins-del';
      var html = insDel
        ? '<ins>' + v.price_money + '</ins> <del class="data-wpd-hide">' + v.compare_money + '</del>'
        : '<del class="data-wpd-hide">' + v.compare_money + '</del> <ins>' + v.price_money + '</ins>';
      if (STR.price_template) html = STR.price_template.replace('INS', v.price_money).replace('DEL', v.compare_money);
      return html + badge;
    }
    return v.price_money;
  };

  Picker.prototype.apply = function (v, oldVariant, initial) {
    if (!v) return;
    var form = this.form, container = this.container, root = this.root;
    var exists = v.exists, available = exists && v.available;

    // 1. The hidden select the ajax add to cart reads.
    if (this.select) {
      var opt = this.select.options[0];
      if (!opt) { opt = document.createElement('option'); this.select.appendChild(opt); }
      opt.value = exists ? String(v.id) : '';
      opt.textContent = v.title || '';
      opt.className = available ? '' : 'is--sold-out';
      opt.selected = true;
      this.select.value = exists ? String(v.id) : '';
    }

    // 2. Add to cart button (main form) - same attributes the theme toggles.
    if (form) {
      var btn = form.querySelector('[type="submit"][name="add"]');
      if (btn) {
        var text = btn.querySelector('.t4s-btn-atc_text');
        if (!exists) {
          btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-disabled', 'true'); btn.removeAttribute('data-atc-form');
          if (text) text.textContent = txt('unavailable', 'Unavailable');
        } else if (!available) {
          btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-disabled', 'true'); btn.removeAttribute('data-atc-form');
          if (text) text.textContent = txt('soldOut', 'Sold out');
        } else {
          btn.removeAttribute('disabled'); btn.removeAttribute('aria-disabled'); btn.setAttribute('data-atc-form', '');
          if (text) text.textContent = txt('addToCart', 'Add to cart'); /* WAVE never shows pre-order wording */
        }
      }
      var pay = form.querySelector('.shopify-payment-button');
      if (pay) pay.style.display = available ? '' : 'none';
      var qtyWrap = form.querySelector('[data-quantity-wrapper]');
      if (qtyWrap) qtyWrap.style.display = available ? '' : 'none';
      var qty = form.querySelector('[data-quantity-value]');
      if (qty) { qty.setAttribute('max', v.max_qty); if (num(qty.value) > v.max_qty) qty.value = 1; }
      var notify = form.querySelector('[data-notify-stock-btn]');
      if (notify) {
        notify.classList.toggle('t4s-d-none', available || !exists);
        if (exists) {
          notify.setAttribute('data-variant-id', v.id);
          notify.setAttribute('data-storageid', 'notify-stock' + v.id);
          notify.setAttribute('data-mfp-src', attr(notify, 'data-root-url', '/') + 'variants/' + v.id + '/?section_id=back-in-stock');
        }
      }
    }

    // 3. Price (same markup the theme's _updatePrice writes) + Klarna / installments.
    if (exists) {
      var priceEls = container.querySelectorAll('[data-product-price]');
      for (var i = 0; i < priceEls.length; i++) {
        var pe = priceEls[i];
        pe.innerHTML = this.priceHtml(v);
        pe.setAttribute('data-wpd-variant-id', v.id);
        pe.setAttribute('data-wpd-variant-price', v.price);
        pe.setAttribute('data-wpd-variant-compare-at-price', v.compare_at_price || '');
        pe.classList.remove('wpd-processed');
      }
      var terms = container.querySelectorAll('shopify-payment-terms');
      for (var t = 0; t < terms.length; t++) terms[t].setAttribute('variant-id', v.id);
      var badgeSale = container.querySelector('[data-badge-sale]');
      if (badgeSale) { if (v.compare_at_price && v.compare_at_price > v.price) badgeSale.removeAttribute('hidden'); else badgeSale.setAttribute('hidden', ''); }
      var badgeSold = container.querySelector('[data-badge-soldout]');
      if (badgeSold) { if (available) badgeSold.setAttribute('hidden', ''); else badgeSold.removeAttribute('hidden'); }
      var badgePre = container.querySelector('[data-badge-preorder]');
      if (badgePre) { if (v.preorder) badgePre.removeAttribute('hidden'); else badgePre.setAttribute('hidden', ''); }
    }

    // 4. SKU / barcode / availability text.
    var skuWrap = container.querySelector('[data-product-sku]');
    if (skuWrap) { var skuN = skuWrap.querySelector('[data-product__sku-number]'); if (skuN) skuN.textContent = v.sku || ''; skuWrap.classList.toggle('t4s-dn', !v.sku); }
    var bcWrap = container.querySelector('[data-product-barcode]');
    if (bcWrap) { var bcN = bcWrap.querySelector('[data-product__barcode-number]'); if (bcN) bcN.textContent = v.barcode || ''; bcWrap.classList.toggle('t4s-dn', !v.barcode); }
    var av = container.querySelector('[data-product-available]');
    if (av) {
      var s1 = av.querySelector('[data-available-status]'), s2 = av.querySelector('[data-soldout-status]');
      var s3 = av.querySelector('[data-instock-status]'), s4 = av.querySelector('[data-preorder-status]');
      if (s1) s1.classList.toggle('t4s-dn', !available);
      if (s2) s2.classList.toggle('t4s-dn', available);
      if (s3) s3.classList.toggle('t4s-dn', v.preorder);
      if (s4) s4.classList.toggle('t4s-dn', !v.preorder);
    }
    var toggles = document.querySelectorAll('[data-variant-toggle]');
    for (var g = 0; g < toggles.length; g++) toggles[g].style.display = (exists && attr(toggles[g], 'data-variant-toggle', '') == String(v.id)) ? '' : 'none';

    // 5. Media: jump the gallery to the variant image (flickity or scroll layouts).
    if (!initial && exists && v.media_id) this.showMedia(v.media_id);

    // 6. Sticky add to cart bar (cloned from #t4s-sticky-atc-temp by the theme).
    if (!this.inQuickView) this.updateSticky(v, available, exists);

    // 7. URL (?variant=), main product page only.
    if (!initial && this.updateHistory && exists && window.history && window.history.replaceState) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        u.searchParams.delete('option_values');
        window.history.replaceState({ path: u.toString() }, '', u.toString());
      } catch (e) { /* ignore */ }
    }

    // 8. Events for the rest of the theme and WAVE's own scripts.
    if (!initial) {
      var detail = { variant: v, oldVariant: oldVariant, form: form, currentVariant: v };
      root.dispatchEvent(new CustomEvent('wave:variant:changed', { bubbles: true, detail: detail }));
      document.dispatchEvent(new CustomEvent('variant:changed', { detail: detail }));
      if (this.select) this.select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      jq(container, { type: 'variant:changed', currentVariant: v, oldVariant: oldVariant });
      var inv = container.querySelector('[data-inventory-qty]');
      if (inv) {
        if (available) { inv.removeAttribute('hidden'); jq(inv, { type: 'variant:inventory', currentVariant: v, oldVariant: oldVariant }); }
        else inv.setAttribute('hidden', '');
      }
      if (container.querySelector('[data-pickup-availability-container]')) {
        jq(container, exists ? { type: 'pickupAvailability:update', currentVariant: v } : { type: 'pickupAvailability:clear', currentVariant: v });
      }
      jq(container, { type: available ? 'notifyBackinStock:hide' : 'notifyBackinStock:show', currentVariant: v });
      jq(document.body, 'currency:update');
    }
  };

  Picker.prototype.showMedia = function (mediaId) {
    var main = this.container.querySelector('[data-main-media]');
    if (!main) return;
    var slide = main.querySelector('[data-media-id="' + mediaId + '"]');
    if (!slide) return;
    var $ = jQ();
    if ($ && main.classList.contains('flickityt4s-enabled')) {
      var slides = main.querySelectorAll('[data-main-slide]');
      var index = Array.prototype.indexOf.call(slides, slide);
      if (index < 0) index = Array.prototype.indexOf.call(main.children, slide);
      try { $(main).flickityt4s('select', index, false, true); return; } catch (e) { /* fall through */ }
    }
    if (slide.scrollIntoView) {
      var r = slide.getBoundingClientRect();
      var visible = r.top >= 0 && r.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      if (!visible) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  Picker.prototype.updateSticky = function (v, available, exists) {
    var bar = document.querySelector('[data-sticky-addtocart]');
    if (!bar) return;
    var price = bar.querySelector('[data-sticky-price]');
    if (price && exists) {
      price.innerHTML = this.priceHtml(v);
      price.setAttribute('data-wpd-variant-id', v.id);
      price.setAttribute('data-wpd-variant-price', v.price);
      price.setAttribute('data-wpd-variant-compare-at-price', v.compare_at_price || '');
      price.classList.remove('wpd-processed');
    }
    var title = bar.querySelector('[data-sticky-v-title]');
    if (title) {
      var span = title.querySelector('[data-wave-sticky-title]');
      if (span) span.textContent = v.title || ''; else if (!title.querySelector('.t4s-dropdown')) title.textContent = v.title || '';
    }
    var atc = bar.querySelector('[data-action-atc]');
    if (atc) {
      if (exists) atc.setAttribute('data-variant-id', v.id);
      var atcText = atc.querySelector('.t4s-btn-atc_text');
      if (atcText) atcText.textContent = !exists ? txt('unavailable', 'Unavailable') : (available ? txt('addToCart', 'Add to cart') : txt('soldOut', 'Sold out'));
      if (available) { atc.removeAttribute('aria-disabled'); atc.removeAttribute('disabled'); }
      else { atc.setAttribute('aria-disabled', 'true'); }
    }
    var qty = bar.querySelector('[data-quantity-value]');
    if (qty) qty.setAttribute('max', v.max_qty);
    var qw = bar.querySelector('[data-quantity-wrapper]');
    if (qw) qw.style.display = available ? '' : 'none';
    var img = bar.querySelector('[data-sticky-img] img');
    if (img && v.image) {
      img.setAttribute('data-src', v.image);
      img.setAttribute('data-orginal', v.image);
      img.src = v.image;
      var wrap = bar.querySelector('[data-sticky-img]');
      if (wrap) wrap.style.background = 'url(' + v.image + ')';
    }
  };

  class WavePicker extends HTMLElement {
    connectedCallback() {
      var self = this;
      // Wait for the form + state markup that follows this tag to be parsed.
      var start = function () {
        if (self.__wavePicker) return;
        if (!self.querySelector('[data-wave-variant-state]') || !self.closest('form')) { setTimeout(start, 30); return; }
        self.__wavePicker = new Picker(self);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
    }
  }
  window.customElements.define('wave-variant-picker', WavePicker);

  /* Sticky bar: the variant title is plain text now (no 250 item dropdown); tapping
     it scrolls the shopper up to the option picker. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-wave-sticky-scroll]');
    if (!t) return;
    var picker = document.querySelector('wave-variant-picker');
    if (picker && picker.scrollIntoView) {
      e.preventDefault();
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
  window.WaveVariantPicker = { readState: readState, instances: function () {
    var out = []; var els = document.querySelectorAll('wave-variant-picker');
    for (var i = 0; i < els.length; i++) if (els[i].__wavePicker) out.push(els[i].__wavePicker);
    return out;
  } };
})();
